import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import PanelValidacion from '../components/common/PanelValidacion';
import GaleriaFotos from '../components/common/GaleriaFotos';
import {
    cctvService,
    sesionActualCCTV,
    descargarReporteCCTV,
    descargarReporteExcelCCTV,
    descargarReporteExcelInspectoriaCCTV,
    descargarPlantillaChecklistCCTV,
} from '../services/cctv';
import toast from 'react-hot-toast';
import {
    Camera,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    XCircle,
    MinusCircle,
    Download,
    Minus,
    History,
    Building2,
    Upload,
    FileSpreadsheet,
    Trash2,
    Globe,
} from 'lucide-react';

const CT_STORAGE_KEY = 'cctv_ct_seleccionado';

// Formato real SST_PR_07 – RE09: cada ítem tiene 3 chequeos independientes.
const OPCIONES_SI_NO_NA = [
    { value: 'si', label: 'SI', icon: CheckCircle2, color: 'text-green-600', activeBg: 'bg-green-50 border-green-400' },
    { value: 'no', label: 'NO', icon: XCircle, color: 'text-red-600', activeBg: 'bg-red-50 border-red-400' },
    { value: 'na', label: 'N/A', icon: MinusCircle, color: 'text-gray-500', activeBg: 'bg-gray-50 border-gray-400' },
];

const OPCIONES_ESTADO_FISICO = [
    { value: 'buen_estado', label: 'BUEN ESTADO', icon: CheckCircle2, color: 'text-green-600', activeBg: 'bg-green-50 border-green-400' },
    { value: 'danado', label: 'DAÑADO', icon: XCircle, color: 'text-red-600', activeBg: 'bg-red-50 border-red-400' },
];

// ─── Grupo de botones reutilizable (Operatividad / Área Despejada / Estado Físico) ──

const GrupoChequeo = ({ titulo, opciones, valor, onSeleccionar, disabled = false }) => (
    <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-24 flex-shrink-0">{titulo}</span>
        <div className="flex items-center gap-1.5">
            {opciones.map((op) => {
                const Icon = op.icon;
                const activo = valor === op.value;
                return (
                    <button
                        key={op.value}
                        onClick={() => onSeleccionar(op.value)}
                        disabled={disabled}
                        className={`flex items-center px-2 py-1 rounded-lg border text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            activo ? `${op.activeBg} ${op.color} border-2` : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                        <Icon className={`h-3 w-3 mr-1 ${activo ? op.color : 'text-gray-400'}`} />
                        {op.label}
                    </button>
                );
            })}
        </div>
    </div>
);

// ─── Item de inspección (3 chequeos independientes) ────────────────────────────

const ItemCCTV = ({ item, sesion, ct, onActualizar, esSesionHistorica = false }) => {
    const [obs, setObs] = useState(item.observaciones || '');
    const [guardandoObs, setGuardandoObs] = useState(false);

    const handleChequeo = async (campo, valor) => {
        try {
            const resultado = await cctvService.responder(item.id, null, null, sesion, ct, { [campo]: valor });
            onActualizar(item.id, { [campo]: valor });
            if (resultado?.solicitud_error) {
                toast.error(resultado.solicitud_error);
            }
        } catch {
            toast.error('Error al guardar');
        }
    };

    const handleGuardarObs = async () => {
        const completo = item.operatividad && item.area_despejada && item.estado_fisico;
        if (!completo) { toast.error('Completa los 3 chequeos primero'); return; }
        setGuardandoObs(true);
        try {
            await cctvService.responder(item.id, null, obs || null, sesion, ct, {});
            onActualizar(item.id, { observaciones: obs || null });
            toast.success('Observación guardada');
        } catch {
            toast.error('Error al guardar');
        } finally {
            setGuardandoObs(false);
        }
    };

    const completo = item.operatividad && item.area_despejada && item.estado_fisico;
    const tieneFalla = item.operatividad === 'no' || item.area_despejada === 'no' || item.estado_fisico === 'danado';
    const rowBg = tieneFalla ? 'bg-red-50' : completo ? 'bg-green-50' : '';

    return (
        <div className={`border border-gray-100 rounded-lg p-3 mb-2 transition-colors ${rowBg}`}>
            <div className="mb-2">
                <span className="text-xs font-bold text-gray-400 mr-2">{item.numero}</span>
                <span className="text-sm text-gray-800">{item.texto}</span>
            </div>

            <div className="space-y-1.5">
                <GrupoChequeo
                    titulo="Operatividad"
                    opciones={OPCIONES_SI_NO_NA}
                    valor={item.operatividad}
                    onSeleccionar={(v) => handleChequeo('operatividad', v)}
                    disabled={esSesionHistorica}
                />
                <GrupoChequeo
                    titulo="Área despejada"
                    opciones={OPCIONES_SI_NO_NA}
                    valor={item.area_despejada}
                    onSeleccionar={(v) => handleChequeo('area_despejada', v)}
                    disabled={esSesionHistorica}
                />
                <GrupoChequeo
                    titulo="Estado físico"
                    opciones={OPCIONES_ESTADO_FISICO}
                    valor={item.estado_fisico}
                    onSeleccionar={(v) => handleChequeo('estado_fisico', v)}
                    disabled={esSesionHistorica}
                />
            </div>

            <div className="mt-2 flex gap-2">
                <input
                    type="text"
                    value={obs}
                    onChange={(e) => setObs(e.target.value)}
                    placeholder="Observaciones (opcional)..."
                    readOnly={esSesionHistorica}
                    className={`flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white ${
                        esSesionHistorica ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                    onKeyDown={(e) => e.key === 'Enter' && handleGuardarObs()}
                />
                {!esSesionHistorica && obs !== (item.observaciones || '') && (
                    <button
                        onClick={handleGuardarObs}
                        disabled={guardandoObs}
                        className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                    >
                        {guardandoObs ? '...' : 'Guardar'}
                    </button>
                )}
            </div>

            <GaleriaFotos modulo="cctv" itemId={item.id} sesion={sesion} ct={ct} soloLectura={esSesionHistorica} />
        </div>
    );
};

// ─── Acordeón de sección ──────────────────────────────────────────────────────

const SeccionAccordion = ({ seccion, sesion, ct, onActualizar, esSesionHistorica = false }) => {
    const [abierto, setAbierto] = useState(false);
    const pct = seccion.total > 0 ? Math.round((seccion.respondidos / seccion.total) * 100) : 0;
    const completo = seccion.respondidos === seccion.total && seccion.total > 0;

    return (
        <div className={`border rounded-xl overflow-hidden mb-3 ${completo ? 'border-green-300' : 'border-gray-200'}`}>
            <button
                onClick={() => setAbierto(!abierto)}
                className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${
                    completo ? 'bg-green-50 hover:bg-green-100' : 'bg-white hover:bg-gray-50'
                }`}
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                        completo ? 'bg-green-200 text-green-800' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                        {seccion.numero}
                    </span>
                    <span className="font-semibold text-gray-800 text-sm truncate">{seccion.titulo}</span>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <div className="hidden sm:flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all ${completo ? 'bg-green-500' : 'bg-indigo-500'}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                            {seccion.respondidos}/{seccion.total}
                        </span>
                    </div>
                    {completo && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />}
                    {abierto ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
            </button>

            {abierto && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4">
                    {seccion.items.map((item) => (
                        <ItemCCTV
                            key={item.id}
                            item={item}
                            sesion={sesion}
                            ct={ct}
                            onActualizar={(id, cambios) => onActualizar(seccion.id, id, cambios)}
                            esSesionHistorica={esSesionHistorica}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Página principal ─────────────────────────────────────────────────────────

const CCTVPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [secciones, setSecciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [descargandoExcel, setDescargandoExcel] = useState(false);
    const [descargandoExcelInspectoria, setDescargandoExcelInspectoria] = useState(false);
    const [cts, setCts] = useState([]);
    const [subiendoExcel, setSubiendoExcel] = useState(false);
    const excelInputRef = useRef(null);

    const sesionParam = searchParams.get('sesion');
    const ctParam = searchParams.get('ct');
    const sesion = sesionParam || sesionActualCCTV();
    const ct = ctParam || (typeof window !== 'undefined' ? localStorage.getItem(CT_STORAGE_KEY) : '') || '';
    const esSesionHistorica = !!sesionParam && sesionParam !== sesionActualCCTV();

    const setCt = (nuevoCt) => {
        const next = new URLSearchParams(searchParams);
        if (nuevoCt) {
            next.set('ct', nuevoCt);
            try { localStorage.setItem(CT_STORAGE_KEY, nuevoCt); } catch {}
        } else {
            next.delete('ct');
            try { localStorage.removeItem(CT_STORAGE_KEY); } catch {}
        }
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        (async () => {
            try {
                const lista = await cctvService.listarCts();
                setCts(lista);
            } catch {
                toast.error('No se pudo cargar la lista de CTs');
            }
        })();
    }, []);

    const cargar = async () => {
        if (!ct) { setSecciones([]); return; }
        setCargando(true);
        try {
            const data = await cctvService.checklist(ct, sesion);
            setSecciones(data);
        } catch {
            toast.error('Error al cargar el checklist CCTV');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [sesion, ct]);

    const handleActualizar = useCallback((seccionId, itemId, cambios) => {
        setSecciones((prev) =>
            prev.map((sec) => {
                if (sec.id !== seccionId) return sec;
                const nuevosItems = sec.items.map((it) =>
                    it.id === itemId ? { ...it, ...cambios } : it
                );
                const respondidos = nuevosItems.filter(
                    (it) => it.operatividad && it.area_despejada && it.estado_fisico
                ).length;
                return { ...sec, items: nuevosItems, respondidos };
            })
        );
    }, []);

    const handleDescargar = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        setDescargando(true);
        try {
            await descargarReporteCCTV(ct, sesion);
            toast.success('Registro CCTV descargado');
        } catch {
            toast.error('Error al descargar el registro');
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarExcel = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        setDescargandoExcel(true);
        try {
            await descargarReporteExcelCCTV(ct, sesion);
            toast.success('Excel CCTV descargado');
        } catch {
            toast.error('Error al descargar el Excel');
        } finally {
            setDescargandoExcel(false);
        }
    };

    const handleDescargarExcelInspectoria = async () => {
        setDescargandoExcelInspectoria(true);
        try {
            await descargarReporteExcelInspectoriaCCTV(sesion);
            toast.success('Excel de toda la inspectoria descargado');
        } catch {
            toast.error('Error al descargar el Excel de la inspectoria');
        } finally {
            setDescargandoExcelInspectoria(false);
        }
    };

    const handleDescargarPlantilla = async () => {
        try {
            await descargarPlantillaChecklistCCTV();
        } catch {
            toast.error('Error al descargar la plantilla');
        }
    };

    const handleSubirExcel = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        setSubiendoExcel(true);
        try {
            const resultado = await cctvService.importarChecklistExcel(ct, file);
            toast.success(resultado.message || 'Checklist actualizado');
            await cargar();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al cargar el Excel');
        } finally {
            setSubiendoExcel(false);
            if (excelInputRef.current) excelInputRef.current.value = '';
        }
    };

    const handleEliminarChecklist = async () => {
        if (!ct) { toast.error('Selecciona un CT primero'); return; }
        if (!window.confirm(`¿Eliminar el checklist CCTV cargado para ${ct}?`)) return;
        try {
            const resultado = await cctvService.eliminarChecklist(ct);
            toast.success(resultado.message || 'Checklist eliminado');
            await cargar();
        } catch {
            toast.error(`${ct} no tiene checklist CCTV cargado`);
        }
    };

    const totalItems = secciones.reduce((s, sec) => s + sec.total, 0);
    const totalResp = secciones.reduce((s, sec) => s + sec.respondidos, 0);
    const pctGlobal = totalItems > 0 ? Math.round((totalResp / totalItems) * 100) : 0;

    // Resumen: cuenta ítems con alguna falla (operatividad/área NO, o estado dañado).
    let totalConFalla = 0;
    secciones.forEach((sec) => sec.items.forEach((it) => {
        if (it.operatividad === 'no' || it.area_despejada === 'no' || it.estado_fisico === 'danado') totalConFalla += 1;
    }));

    const tieneChecklist = !cargando && secciones.length > 0;
    const sinChecklist = ct && !cargando && secciones.length === 0;

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Camera className="h-7 w-7 text-indigo-600" />
                            Inspección CCTV
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Sesión: <span className="font-semibold text-indigo-600">{sesion}</span>
                            {ct && <> · CT: <span className="font-semibold text-indigo-600">{ct}</span></>}
                            {' · '}SST_PR_07 – RE09
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => navigate('/inspeccion-cctv/historial')}
                            className="flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm shadow-sm"
                        >
                            <History className="h-4 w-4 mr-2" />
                            Historial
                        </button>
                        <button
                            onClick={handleDescargar}
                            disabled={descargando || !ct}
                            className="flex items-center px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargando
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Download className="h-4 w-4 mr-2" />}
                            {descargando ? 'Descargando...' : 'Descargar PDF'}
                        </button>
                        <button
                            onClick={handleDescargarExcel}
                            disabled={descargandoExcel || !ct}
                            className="flex items-center px-4 py-2.5 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargandoExcel
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                            {descargandoExcel ? 'Descargando...' : 'Descargar Excel'}
                        </button>
                        <button
                            onClick={handleDescargarExcelInspectoria}
                            disabled={descargandoExcelInspectoria}
                            title="Excel consolidado con todos los Centros de Trabajo de la inspectoria"
                            className="flex items-center px-4 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-800 disabled:opacity-60 transition-colors font-semibold text-sm"
                        >
                            {descargandoExcelInspectoria
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Globe className="h-4 w-4 mr-2" />}
                            {descargandoExcelInspectoria ? 'Descargando...' : 'Excel toda la inspectoria'}
                        </button>
                    </div>
                </div>

                {/* Selector de CT */}
                <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-gray-700">
                            <Building2 className="h-5 w-5 text-indigo-600" />
                            <span className="text-sm font-semibold">Centro de Trabajo:</span>
                        </div>
                        <select
                            value={ct}
                            onChange={(e) => setCt(e.target.value)}
                            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">— Selecciona un CT —</option>
                            {cts.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        {ct && (
                            <span className="text-xs text-gray-500">
                                Cada CT tiene su propio checklist (cargado vía Excel)
                            </span>
                        )}
                    </div>

                    {/* Carga del checklist propio del CT seleccionado */}
                    {ct && (
                        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">
                                Checklist de {ct}:
                            </span>
                            <button
                                onClick={handleDescargarPlantilla}
                                className="flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium"
                                title="Descargar plantilla Excel"
                            >
                                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                                Descargar plantilla
                            </button>
                            <button
                                onClick={() => excelInputRef.current?.click()}
                                disabled={subiendoExcel}
                                className="flex items-center px-3 py-1.5 bg-indigo-50 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-60 transition-colors text-xs font-medium"
                                title="Subir checklist Excel de este CT"
                            >
                                {subiendoExcel
                                    ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-indigo-700 mr-1.5" />
                                    : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                                {subiendoExcel ? 'Cargando...' : 'Subir Excel'}
                            </button>
                            <input
                                ref={excelInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleSubirExcel}
                                className="hidden"
                            />
                            {tieneChecklist && (
                                <button
                                    onClick={handleEliminarChecklist}
                                    className="flex items-center px-3 py-1.5 bg-white border border-gray-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs font-medium"
                                    title="Eliminar checklist de este CT"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                    Eliminar checklist
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {ct && <PanelValidacion modulo="cctv" sesion={sesion} ct={ct} />}

                {/* Banner sesión histórica */}
                {esSesionHistorica && (
                    <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center gap-2 text-amber-800 text-sm">
                            <History className="h-4 w-4" />
                            <span>Estás viendo una sesión <strong>histórica</strong>. Las respuestas no se modifican aquí.</span>
                        </div>
                        <button
                            onClick={() => navigate('/inspeccion-cctv')}
                            className="text-amber-800 text-xs font-semibold underline hover:no-underline"
                        >
                            Ir a la sesión actual
                        </button>
                    </div>
                )}

                {/* Resumen global */}
                {totalItems > 0 && (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
                                <p className="text-xl font-bold text-green-700">{totalResp}</p>
                                <p className="text-xs text-green-600">Completos</p>
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                                <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                                <p className="text-xl font-bold text-red-700">{totalConFalla}</p>
                                <p className="text-xs text-red-600">Con falla</p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                                <Minus className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-gray-600">{totalItems - totalResp}</p>
                                <p className="text-xs text-gray-500">Pendientes</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progreso general</span>
                                <span>{totalResp}/{totalItems} respondidos — {pctGlobal}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                    className="bg-indigo-500 h-2.5 rounded-full transition-all duration-500"
                                    style={{ width: `${pctGlobal}%` }}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Checklist por secciones */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Camera className="h-5 w-5 text-indigo-500" />
                        Aspectos a verificar — {secciones.length} secciones
                    </h2>

                    {!ct && (
                        <div className="text-center py-14 text-gray-400">
                            <Building2 className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">Selecciona un Centro de Trabajo para iniciar.</p>
                        </div>
                    )}

                    {ct && cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                        </div>
                    )}

                    {sinChecklist && (
                        <div className="text-center py-14 text-gray-400">
                            <Camera className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">{ct} no tiene un checklist cargado todavía.</p>
                            <p className="text-xs mt-1">Descarga la plantilla y súbela con el botón "Subir Excel" de arriba.</p>
                        </div>
                    )}

                    {ct && !cargando && secciones.map((sec) => (
                        <SeccionAccordion
                            key={sec.id}
                            seccion={sec}
                            sesion={sesion}
                            ct={ct}
                            onActualizar={handleActualizar}
                            esSesionHistorica={esSesionHistorica}
                        />
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default CCTVPage;
